import { Alert, Box, IconButton, InputAdornment, Link, Snackbar, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Link as RouterLink } from "react-router-dom"
import { FORGOT_PASSWORD_ROUTE, ROOT_ROUTE, USER_NAME_ROUTE } from "../../../constants/routes"
import { Field, Form, Formik, FormikHelpers } from "formik"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { FormikCheckbox } from "../../../shared/components/Formik/FormikCheckbox"
import { FormikSubmitButton } from "../../../shared/components/forms/Formik"
import * as yup from "yup"
import { useAppDispatch, useAppSelector } from "../../store/storeHooks"
import { selectUsername } from "../loginSelectors"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { useEffect, useState } from "react"
import { Authentication, isAuthenticationComplete } from "../loginTypes"
import { login } from "../loginSlice"
import { useNavigate } from "react-router"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { authenticateByPassword } from "../loginApi"
import { useLocationState } from "../../../core/router/routerHooks"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { BackButton } from "../../../shared/components/buttons/BackButton"

const useStyles = makeStyles()(() => ({
    formContainer: {
        maxWidth: "25rem",
        width: "100%",
        margin: "auto",
    },
    welcomeText: {
        textAlign: "left",
        marginBottom: "0.5rem",
        fontWeight: 400,
        fontSize: "2.5rem",
    },
    welcomeSubText: {
        textAlign: "left",
        marginBottom: "1rem",
        color: "rgba(0, 0, 0, 0.6)",
    },
    submitButton: {
        marginTop: "1rem",
        padding: "0.75rem",
        backgroundColor: "#000",
        color: "#fff",
        borderRadius: "3rem",
        "&:hover": {
            backgroundColor: "#333",
        },
    },
}))

type SignInWithPasswordProps = {}

type FormValues = {
    password: string
}

type SignInWithPasswordStateLenient = Readonly<{
    authentication: Authentication | undefined
}>

export function SignInWithPassword({}: SignInWithPasswordProps) {
    const { classes } = useStyles()
    const [errorMessage, setErrorMessage] = useState("Something went wrong")
    const [showErrorToaster, setShowErrorToaster] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const { authentication } = useLocationState<SignInWithPasswordStateLenient>({ authentication: undefined })
    const navigate = useNavigate()

    const { l10n } = useAppLocalization()
    const username = useAppSelector(selectUsername)
    const dispatch = useAppDispatch()

    const validationSchema = yup.object({
        password: yup.string().required(l10n.getString("sign-in-with-password-required")),
    })

    const initialValues: FormValues = { password: "" }

    const { mutateAsync: callAuthenticateByPassword } = usePublicApiMutation(authenticateByPassword, {})

    const hasAuthPrerequisites = Boolean(authentication) && Boolean(username)

    useEffect(() => {
        if (!hasAuthPrerequisites) {
            navigate(USER_NAME_ROUTE, { replace: true })
        }
    }, [hasAuthPrerequisites, navigate])

    const onSubmit = async ({ password }: FormValues, _helpers: FormikHelpers<FormValues>) => {
        if (!authentication || !username) {
            navigate(USER_NAME_ROUTE, { replace: true })
            return
        }

        const response = await callAuthenticateByPassword({ username, password, authentication })

        if (isAuthenticationComplete(response)) {
            dispatch(
                login({
                    authToken: response.authentication.token,
                    user: response.user,
                }),
            )

            navigate(ROOT_ROUTE)
        } else {
            setShowErrorToaster(true)
            setErrorMessage(
                `Client expected authentication complete but server said: ${response.authenticationResult.action}`,
            )
        }
    }

    // Avoid flashing a non-functional form for the frame before the redirect above runs.
    if (!hasAuthPrerequisites) {
        return null
    }

    return (
        <Box className={classes.formContainer}>
            <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
                {({ errors, touched, handleSubmit, isValid }) => (
                    <Box component={Form} onSubmit={handleSubmit}>
                        <LocalizedStrict id="sign-in-with-username-title">
                            <Typography component="h1" variant="h3" className={classes.welcomeText}>
                                Welcome
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="sign-in-with-username-subtitle">
                            <Typography component="p" className={classes.welcomeSubText}>
                                Welcome to Facilitynet
                            </Typography>
                        </LocalizedStrict>
                        <Stack direction="column" spacing=".25rem">
                            <LocalizedStrict id="sign-in-with-password-field-label" attrs={{ label: true }}>
                                <Field
                                    as={TextField}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    label="Password"
                                    fullWidth
                                    variant="outlined"
                                    error={touched.password && !!errors.password}
                                    helperText={touched.password && errors.password}
                                    autoComplete="current-password"
                                />
                            </LocalizedStrict>

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <LocalizedStrict id="sign-in-with-username-remember-me">
                                    <FormikCheckbox name="rememberUsername" color="primary">
                                        Remember me
                                    </FormikCheckbox>
                                </LocalizedStrict>
                                <LocalizedStrict id="sign-in-with-username-reset-password-link">
                                    <Link component={RouterLink} to={FORGOT_PASSWORD_ROUTE} variant="body2">
                                        Forgot password?
                                    </Link>
                                </LocalizedStrict>
                            </Stack>
                        </Stack>
                        <LocalizedStrict id="sign-in-with-password-submit-button">
                            <FormikSubmitButton
                                className={classes.submitButton}
                                variant="contained"
                                sx={{ marginTop: 3, marginBottom: 2 }}
                            >
                                Sign in
                            </FormikSubmitButton>
                        </LocalizedStrict>
                        <BackButton />
                    </Box>
                )}
            </Formik>
            <Snackbar
                open={showErrorToaster}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={5000}
                onClose={() => setShowErrorToaster(false)}
            >
                <Alert
                    onClose={() => setShowErrorToaster(false)}
                    sx={{ marginBottom: "2rem", marginRight: "1rem" }}
                    severity="error"
                    variant="filled"
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}
